import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Building2, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";

export default function SuperTenants() {
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    name: "", brand_name: "", phone: "",
    primary_color: "#0F172A", accent_color: "#D4AF37",
    subscription_amount: 9999, renewal_amount: 9999,
  });

  const load = () => api.get("/super-admin/tenants").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.phone) return toast.error("Name & phone required");
    try {
      await api.post("/super-admin/tenants", {
        ...form,
        subscription_amount: parseFloat(form.subscription_amount),
        renewal_amount: parseFloat(form.renewal_amount),
      });
      toast.success("Tenant created");
      setShow(false); load();
      setForm({ name: "", brand_name: "", phone: "", primary_color: "#0F172A", accent_color: "#D4AF37", subscription_amount: 9999, renewal_amount: 9999 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (t) => {
    const newStatus = t.status === "active" ? "suspended" : "active";
    await api.patch(`/super-admin/tenants/${t.id}`, { status: newStatus });
    toast.success(`Tenant ${newStatus}`); load();
  };

  return (
    <div data-testid="super-tenants-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Tenants</h1>
          <p className="text-sm text-slate-500">{list.length} tenant(s)</p>
        </div>
        <button data-testid="create-tenant-btn" onClick={() => setShow(true)} className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-xl text-sm font-bold tap-scale">
          <Plus className="w-4 h-4" /> Create Tenant
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((t) => (
          <div key={t.id} data-testid={`tenant-card-${t.id}`} className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl grid place-items-center text-white" style={{ background: t.primary_color || "#0F172A" }}>
                <Building2 className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold tracking-tight truncate">{t.brand_name || t.name}</div>
                <div className="text-[11px] text-slate-500">+91 {t.phone}</div>
              </div>
              <span className={`text-[10px] uppercase font-bold px-2.5 py-1 rounded-full ${t.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{t.status}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-[10px] uppercase font-bold text-slate-400">Subscription</div>
                <div className="font-bold">{fmtINR(t.subscription_amount)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-slate-400">Due</div>
                <div className="font-bold text-rose-600">{fmtINR(t.due_amount)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-slate-400">Status</div>
                <div className="font-bold capitalize">{t.payment_status}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-slate-400">Expires</div>
                <div className="font-bold">{fmtDate(t.subscription_end)}</div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Link to={`/super-admin/tenants/${t.id}`} data-testid={`view-tenant-${t.id}`} className="flex-1 text-center bg-tenant-primary text-white py-2 rounded-lg font-bold text-xs">Manage</Link>
              <button data-testid={`toggle-${t.id}`} onClick={() => toggle(t)} className={`flex-1 py-2 rounded-lg font-bold text-xs ${t.status === "active" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                {t.status === "active" ? "Suspend" : "Activate"}
              </button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="col-span-full text-center text-slate-400 py-12">No tenants yet.</div>}
      </div>

      {show && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={() => setShow(false)}>
          <div className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <div className="font-extrabold text-lg">Create Tenant</div>
              <button onClick={() => setShow(false)}><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <Input label="Tenant Name *" testId="tnt-name" v={form.name} set={(v) => setForm({ ...form, name: v })} />
              <Input label="Brand Name" testId="tnt-brand" v={form.brand_name} set={(v) => setForm({ ...form, brand_name: v })} />
              <Input label="Admin Phone *" testId="tnt-phone" v={form.phone} set={(v) => setForm({ ...form, phone: v.replace(/\D/g, "") })} />
              <div className="grid grid-cols-2 gap-2">
                <Input label="Primary Color" testId="tnt-primary" v={form.primary_color} set={(v) => setForm({ ...form, primary_color: v })} type="color" />
                <Input label="Accent Color" testId="tnt-accent" v={form.accent_color} set={(v) => setForm({ ...form, accent_color: v })} type="color" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input label="Subscription ₹" testId="tnt-sub" v={form.subscription_amount} set={(v) => setForm({ ...form, subscription_amount: v })} type="number" />
                <Input label="Renewal ₹" testId="tnt-renewal" v={form.renewal_amount} set={(v) => setForm({ ...form, renewal_amount: v })} type="number" />
              </div>
              <button data-testid="confirm-create-tenant" onClick={create} className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Input = ({ label, testId, v, set, type = "text" }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{label}</label>
    <input type={type} data-testid={testId} value={v} onChange={(e) => set(e.target.value)} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-slate-900" />
  </div>
);
